import { useEffect, useRef, useState } from 'react';
import { DEFAULT_LEARN_MORE_CONTENT } from '@act/shared/data-transfer-objects';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PublishOutlinedIcon from '@mui/icons-material/PublishOutlined';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';

import { useForm } from 'react-hook-form';
import {
  resetCurrentSurveyContent,
  setCurrentSurveyContent,
  surveyCreatorSelectors,
  upsertSurveyRecord,
} from 'redux/slices/surveyCreatorSlice';
import { learnMoreCreatorSelectors } from 'redux/slices/learnMoreCreatorSlice';
import { surveyAdminRequests } from 'redux/requests/survey-admin.requests';
import { useAppDispatch } from 'redux/store-hooks';
import { useApiCall } from 'redux/utils/api-hook';
import { handleDeletion } from './utils/handleDeletion';
import { FormBuilder } from '@act/storybook';
import QuestionList from './QuestionList';
import useConfirmationAlertDialog from 'react-components/dialog-forms/hooks/use-confirmation-alert-dialog';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSurveyFormConfig } from './survey-form-config';
import { useToast } from '@act/react-components/app/ReusableToast';
import { SurveyV2 } from '@act/shared/data-transfer-objects';

interface DeleteDialogState {
  open: boolean;
  type: 'question' | 'option' | 'checkpoint' | 'prompt' | 'survey';
  questionIndex: number;
  optionIndex?: number;
}

const SurveyCreator = () => {
  const openConfirmationDialog = useConfirmationAlertDialog();
  const [open, setOpen] = useState<boolean>(true);
  const toast = useToast();
  const dispatch = useAppDispatch();

  const [surveyRecord, isSavingRecord, saveSurveyRecord] = useApiCall(
    surveyAdminRequests.createSurveyCreatorRecord
  );
  const [updatedSurveyRecord, isUpdatingRecord, updateSurveyRecord] =
    useApiCall(surveyAdminRequests.updateSurveyCreatorRecord);

  const [publishedSurveyRecord, isPublishingRecord, publishSurveyRecord] =
    useApiCall(surveyAdminRequests.publishSurveyCreatorRecord);

  const currentContent =
    surveyCreatorSelectors.useSelectSurveyCreatorCurrentContent();

  const allSurveyRecords =
    surveyCreatorSelectors.useSelectSurveyCreatorRecords();
  const allLearnMoreRecords =
    learnMoreCreatorSelectors.useSelectLearnMoreRecords();

  const [deleteDialog, setDeleteDialog] = useState<DeleteDialogState>({
    open: false,
    type: 'question',
    questionIndex: -1,
  });

  const generateSurveyKey = (name: string) => {
    return name.toLowerCase().replace(/ /g, '-');
  };

  const { control, reset, watch, setValue, handleSubmit, formState } = useForm<
    SurveyV2.CreateSurveyJSONDto | SurveyV2.UpdateSurveyJSONDto
  >({
    mode: 'onChange',
    resolver: zodResolver(SurveyV2.CreateSurveyJSONSchema),
    defaultValues: {
      key:
        currentContent?.key ??
        generateSurveyKey(currentContent?.surveyContent?.name || ''),
      surveyContent: {
        id: currentContent?.surveyContent?.id ?? -1,
        name: currentContent?.surveyContent?.name ?? '',
        key:
          currentContent?.surveyContent?.key ??
          generateSurveyKey(currentContent?.surveyContent?.name || ''),
        isActionSurvey: currentContent?.surveyContent?.isActionSurvey ?? false,
        displayName: currentContent?.surveyContent?.displayName ?? '',
        summary: currentContent?.surveyContent?.summary ?? '',
        surveyVersion: {
          defaultFinalCheckpointTitle:
            currentContent?.surveyContent?.surveyVersion
              ?.defaultFinalCheckpointTitle ?? '',
          defaultFinalCheckpointMessage:
            currentContent?.surveyContent?.surveyVersion
              ?.defaultFinalCheckpointMessage ?? '',
          defaultFinalCheckpointSummary:
            currentContent?.surveyContent?.surveyVersion
              ?.defaultFinalCheckpointSummary ?? '',
          initialQuestionKey:
            currentContent?.surveyContent?.surveyVersion?.initialQuestionKey ??
            '',
          questions:
            currentContent?.surveyContent?.surveyVersion?.questions ?? [],
        },
        learnMoreContent: {
          title: currentContent?.surveyContent?.learnMoreContent?.title ?? '',
          sections:
            currentContent?.surveyContent?.learnMoreContent?.sections ?? [],
        },
        assignmentConfiguration:
          currentContent?.surveyContent?.assignmentConfiguration ?? null,
      },
      selectedSurveyCreatorRecordId:
        currentContent?.selectedSurveyCreatorRecordId ?? -1,
      selectedLearnMoreRecordId:
        currentContent?.selectedLearnMoreRecordId ?? -1,
    },
  });

  const currentSelectRecordId = watch('selectedSurveyCreatorRecordId');
  const currentLearnMoreRecordId = watch('selectedLearnMoreRecordId');
  const formData = watch();

  const formConfig = useSurveyFormConfig({
    allSurveyRecords,
    allLearnMoreRecords,
    currentContent,
  });

  useEffect(() => {
    const surveyName = formData.surveyContent?.name;
    if (surveyName && surveyName !== '') {
      const newKey = generateSurveyKey(surveyName);
      setValue('surveyContent.key', newKey);
      setValue('key', newKey);
    }
  }, [formData.surveyContent?.name, setValue]);

  // Only reset when switching between different survey records (not on every content change)
  const [lastLoadedRecordId, setLastLoadedRecordId] = useState<number>(
    currentContent?.selectedSurveyCreatorRecordId ?? currentContent.id ?? -1
  );

  useEffect(() => {
    // Only reset if we're loading a completely different record
    if (currentSelectRecordId !== lastLoadedRecordId) {
      // If selected no record, reset to blank form
      if (currentSelectRecordId === -1 && lastLoadedRecordId !== -1) {
        reset({
          key: '',
          surveyContent: {
            name: '',
            key: '',
            isActionSurvey: false,
            displayName: '',
            summary: '',
            surveyVersion: {
              defaultFinalCheckpointTitle: '',
              defaultFinalCheckpointMessage: '',
              defaultFinalCheckpointSummary: '',
              initialQuestionKey: '',
              questions: [],
            },
            learnMoreContent: {
              title: '',
              sections: [],
            },
            assignmentConfiguration: null,
          },
          selectedLearnMoreRecordId: -1,
          selectedSurveyCreatorRecordId: -1,
        });
        dispatch(resetCurrentSurveyContent());
        setLastLoadedRecordId(-1);
      } else if (
        currentSelectRecordId !== -1 &&
        lastLoadedRecordId !== currentSelectRecordId
      ) {
        const surveyContent = allSurveyRecords.find(
          record => record.id === currentSelectRecordId
        )?.surveyContent;

        if (surveyContent) {
          const processedContent = {
            ...surveyContent,
            surveyVersion: {
              ...surveyContent.surveyVersion,
              questions: surveyContent.surveyVersion.questions.map(
                question => ({
                  ...question,
                  isRequired:
                    question.isRequired ||
                    question.validationConfiguration?.isRequired ||
                    false,
                })
              ),
            },
          };
          reset({
            key: surveyContent.key,
            surveyContent: processedContent,
            selectedSurveyCreatorRecordId: currentSelectRecordId,
            selectedLearnMoreRecordId: currentLearnMoreRecordId,
          });
          dispatch(
            setCurrentSurveyContent({
              id: currentSelectRecordId,
              surveyContent: processedContent,
              selectedSurveyCreatorRecordId: currentSelectRecordId,
              selectedLearnMoreRecordId: currentLearnMoreRecordId,
            })
          );

          setLastLoadedRecordId(currentSelectRecordId);
        }
      }
    }
  }, [
    currentSelectRecordId,
    lastLoadedRecordId,
    allSurveyRecords,
    currentLearnMoreRecordId,
    reset,
    dispatch,
  ]);
  useEffect(() => {
    if (updatedSurveyRecord) {
      dispatch(upsertSurveyRecord(updatedSurveyRecord));
      toast('Survey Record updated successfully');
    }
  }, [updatedSurveyRecord, dispatch]);

  useEffect(() => {
    if (publishedSurveyRecord) {
      dispatch(upsertSurveyRecord(publishedSurveyRecord));
      toast('Survey Record published successfully');
    }
  }, [publishedSurveyRecord, dispatch]);

  useEffect(() => {
    const learnMoreContent =
      currentLearnMoreRecordId === -1
        ? DEFAULT_LEARN_MORE_CONTENT
        : allLearnMoreRecords.find(
            record => record.id === currentLearnMoreRecordId
          )?.learnMoreContent;

    if (learnMoreContent) {
      setValue('surveyContent.learnMoreContent', learnMoreContent);
    }
  }, [allLearnMoreRecords, currentLearnMoreRecordId, setValue]);

  const isInitialMount = useRef(true);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    dispatch(
      setCurrentSurveyContent({
        id: formData.surveyContent?.id ?? -1,
        key: formData.key,
        surveyContent: {
          id: formData.surveyContent?.id ?? -1,
          name: formData.surveyContent.name,
          key: formData.surveyContent.key,
          isActionSurvey: formData.surveyContent.isActionSurvey,
          displayName: formData.surveyContent.displayName,
          summary: formData.surveyContent.summary,
          surveyVersion: {
            ...formData.surveyContent.surveyVersion,
            questions: formData.surveyContent.surveyVersion.questions,
          },
          learnMoreContent: formData.surveyContent.learnMoreContent,
          assignmentConfiguration:
            formData.surveyContent.assignmentConfiguration,
        },
        selectedSurveyCreatorRecordId: formData.selectedSurveyCreatorRecordId,
        selectedLearnMoreRecordId: formData.selectedLearnMoreRecordId,
      })
    );
  }, [
    formData.key,
    formData.surveyContent.name,
    formData.surveyContent.key,
    formData.surveyContent.isActionSurvey,
    formData.surveyContent.displayName,
    formData.surveyContent.summary,
    formData.surveyContent.surveyVersion.questions,
    formData.surveyContent.learnMoreContent,
    formData.surveyContent.assignmentConfiguration,
    formData.selectedSurveyCreatorRecordId,
    formData.selectedLearnMoreRecordId,
    currentContent.id,
    dispatch,
  ]);

  const isPublished = (): boolean => {
    if (!currentContent.id) {
      return false;
    }

    return !!allSurveyRecords.find(record => record.id === currentContent.id)
      ?.publishedAt;
  };

  const handleReset = async () => {
    const shouldResetSurvey = await openConfirmationDialog({
      title: 'Reset survey confirmation',
      icon: <ErrorOutlineIcon />,
      iconColor: 'error',
      iconBorderColor: 'errorContainer',
      message:
        'Are you sure you want to reset this survey? You will lose all unsaved changes, and be starting from scratch.',
      confirmationText: 'Reset survey',
      confirmButtonColor: 'error',
    });

    if (shouldResetSurvey) {
      dispatch(resetCurrentSurveyContent());
      setLastLoadedRecordId(-1);
      isInitialMount.current = true;
      reset({
        key: '',
        surveyContent: {
          name: '',
          key: '',
          isActionSurvey: false,
          displayName: '',
          summary: '',
          surveyVersion: {
            defaultFinalCheckpointTitle: '',
            defaultFinalCheckpointMessage: '',
            defaultFinalCheckpointSummary: '',
            initialQuestionKey: '',
            questions: [],
          },
          learnMoreContent: {
            title: '',
            sections: [],
          },
          assignmentConfiguration: null,
        },
        selectedLearnMoreRecordId: -1,
        selectedSurveyCreatorRecordId: -1,
      });
      toast('Survey reset successfully');
    }
  };

  const handleDuplicate = async () => {
    const duplicateKey = `${formData.surveyContent.key}-copy`;
    const duplicateContent = {
      ...formData.surveyContent,
      name: `${formData.surveyContent.name} (Copy)`,
      key: duplicateKey,
    };

    const shouldDuplicateSurvey = await openConfirmationDialog({
      title: 'Duplicate survey confirmation',
      icon: <ErrorOutlineIcon />,
      iconColor: 'error',
      iconBorderColor: 'errorContainer',
      message: `Are you sure you want to duplicate ${
        formData.surveyContent.name || 'this survey'
      }?`,
      confirmationText: 'Duplicate survey',
      confirmButtonColor: 'error',
    });

    if (shouldDuplicateSurvey) {
      setLastLoadedRecordId(-1);
      isInitialMount.current = true;
      reset({
        key: duplicateKey,
        surveyContent: duplicateContent,
        selectedLearnMoreRecordId: currentLearnMoreRecordId,
        selectedSurveyCreatorRecordId: -1,
      });
      saveSurveyRecord({
        params: null,
        body: {
          key: duplicateKey,
          surveyContent: duplicateContent,
        },
      });
    }
  };

  useEffect(() => {
    if (surveyRecord) {
      dispatch(upsertSurveyRecord(surveyRecord));
      dispatch(
        setCurrentSurveyContent({
          id: surveyRecord.id,
          surveyContent: {
            ...surveyRecord.surveyContent,
          },
        })
      );
      setValue('selectedSurveyCreatorRecordId', surveyRecord.id);
      setLastLoadedRecordId(surveyRecord.id);
      toast('Survey Record created successfully');
    }
  }, [surveyRecord, dispatch, setValue]);

  const handlePublish = async () => {
    if (!currentContent.id) {
      return;
    }

    const shouldPublishSurvey = await openConfirmationDialog({
      title: 'Publish survey confirmation',
      icon: <ErrorOutlineIcon />,
      iconColor: 'error',
      iconBorderColor: 'errorContainer',
      message: `Are you sure you want to publish ${
        formData.surveyContent.name || 'this survey'
      }?`,
      confirmationText: 'Publish survey',
      confirmButtonColor: 'error',
    });

    if (shouldPublishSurvey) {
      publishSurveyRecord({
        params: { id: currentContent.id },
        body: {
          key: formData.key,
          surveyContent: formData.surveyContent,
        },
      });
    }
  };

  const onSubmit = (
    data: SurveyV2.CreateSurveyJSONDto | SurveyV2.UpdateSurveyJSONDto
  ) => {
    const requestData = {
      key: data.key || '',
      surveyContent: data.surveyContent,
    };
    if (currentContent.selectedSurveyCreatorRecordId !== -1) {
      updateSurveyRecord({
        params: { id: currentContent.selectedSurveyCreatorRecordId },
        body: requestData,
      });
    } else {
      saveSurveyRecord({
        params: null,
        body: requestData,
      });
    }
  };

  const handleDeleteClick = (
    type: 'checkpoint' | 'option' | 'prompt' | 'question',
    questionIndex: number,
    optionIndex?: number
  ) => {
    setDeleteDialog({ open: true, type, questionIndex, optionIndex });
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, type: 'question', questionIndex: -1 });
  };

  const handleDeleteConfirm = () => {
    const { type, questionIndex, optionIndex } = deleteDialog;

    const updatedContent = handleDeletion(
      formData,
      type,
      questionIndex,
      optionIndex
    );

    setValue(
      'surveyContent.surveyVersion.questions',
      updatedContent.surveyContent.surveyVersion.questions
    );

    setDeleteDialog({ open: false, type: 'question', questionIndex: -1 });
  };

  const onToggle = () => setOpen(!open);

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box
        component="div"
        sx={{
          padding: '8px 16px',
          backgroundColor: 'background.paper',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6" sx={{ color: 'text.primary' }}>
          Survey Content
        </Typography>
        <Box sx={{ display: 'flex', gap: '8px' }}>
          <Tooltip title="Clear content">
            <IconButton
              onClick={() => {
                handleReset();
              }}
            >
              <ReplayOutlinedIcon />
            </IconButton>
          </Tooltip>
          {Object.hasOwn(currentContent, 'id') && (
            <Tooltip title="Duplicate content">
              <IconButton onClick={handleDuplicate}>
                <FileCopyOutlinedIcon />
              </IconButton>
            </Tooltip>
          )}
          {Object.hasOwn(currentContent, 'id') && (
            <Tooltip
              title={isPublished() ? 'Published content' : 'Publish content'}
            >
              <IconButton onClick={handlePublish}>
                <PublishOutlinedIcon
                  color={isPublished() ? 'success' : 'inherit'}
                />
              </IconButton>
            </Tooltip>
          )}
          <Button
            variant="outlined"
            disabled={
              currentContent.surveyContent?.name === '' ||
              currentContent.surveyContent?.displayName === '' ||
              currentContent.surveyContent?.summary === '' ||
              isSavingRecord ||
              isUpdatingRecord ||
              isPublishingRecord
            }
            onClick={handleSubmit(onSubmit)}
          >
            {isSavingRecord || isUpdatingRecord || isPublishingRecord ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                Saving... <CircularProgress size={16} />
              </Box>
            ) : (
              'Save content'
            )}
          </Button>
        </Box>
      </Box>
      <Box>
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '8px',
            padding: '16px 16px 0',
            marginBottom: open ? '0' : '16px',
          }}
        >
          <Typography variant="h6" sx={{ color: 'text.primary' }}>
            Settings
          </Typography>
          <IconButton onClick={onToggle}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </Box>

        <Collapse in={open} unmountOnExit>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              padding: '24px 16px 16px',
            }}
          >
            <FormBuilder
              config={formConfig}
              control={control}
              formState={formState}
              disabled={
                isSavingRecord || isUpdatingRecord || isPublishingRecord
              }
            />
          </Box>
        </Collapse>
        <Divider />
        <Box sx={{ padding: '16px' }}>
          <QuestionList
            content={formData}
            onQuestionsChange={questions => {
              setValue('surveyContent.surveyVersion.questions', questions);
            }}
            onDeleteQuestion={questionIndex => {
              handleDeleteClick('question', questionIndex);
            }}
            formErrors={formState.errors}
          />
        </Box>
      </Box>
      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">
          {deleteDialog.type === 'survey' && 'Reset Survey'}
          {deleteDialog.type !== 'survey' &&
            `Delete
            ${deleteDialog.type
              .charAt(0)
              .toUpperCase()}${deleteDialog.type.slice(1)}`}
        </DialogTitle>
        <DialogContent sx={{ width: '650px' }}>
          <Typography>
            {deleteDialog.type === 'survey' &&
              `Are you sure you want to reset this survey?`}
            {deleteDialog.type !== 'survey' &&
              `Are you sure you want to delete this ${deleteDialog.type}?`}
            <br />
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SurveyCreator;
